/**
 * KAN-826: card of a client company (/companies/:id): header with the name
 * and the city, requisites (editable when PATCH is open), extra fields from
 * imports (read-only) and the contacts of the company. Data and editing
 * state live in hooks/useClientCompanies.
 */
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Building2, Loader2 } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { ClientCompanyRequisitesCard } from '@/components/clientCompanies/ClientCompanyRequisitesCard';
import { ClientCompanyExtraCard } from '@/components/clientCompanies/ClientCompanyExtraCard';
import { ClientCompanyContactsCard } from '@/components/clientCompanies/ClientCompanyContactsCard';
import { useClientCompany, useClientCompanyEditor } from '@/hooks/useClientCompanies';
import { CRM_FILTER_PARAM, MANAGER_FILTER_ALL } from '@/lib/crmLeadFilters';
import { withCrmTablePeriod } from '@/lib/crmTablePeriod';

const LIST_CRUMB = { label: 'Организации', href: '/companies' };

/**
 * KAN-885: CRM этой организации за всё время (таблица по умолчанию за "Сегодня").
 * KAN-909: и по всем менеджерам (иначе у менеджера открылось бы его умолчание "Мои").
 */
function companyCrmHref(companyId: string): string {
  const params = withCrmTablePeriod(
    new URLSearchParams({ company_id: companyId, [CRM_FILTER_PARAM.manager]: MANAGER_FILTER_ALL }),
    null
  );
  return `/crm?${params.toString()}`;
}

export function ClientCompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: company, isLoading, isError } = useClientCompany(id);
  const editor = useClientCompanyEditor(id, company);

  useEffect(() => {
    document.title = `${company?.name || 'Организация'} - SMETA AI`;
  }, [company?.name]);

  if (isLoading) {
    return (
      <PageLayout title="Организация" breadcrumbs={[LIST_CRUMB]} backButton={{ href: '/companies' }}>
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !company) {
    return (
      <PageLayout title="Организация" breadcrumbs={[LIST_CRUMB]} backButton={{ href: '/companies' }}>
        <p className="py-8 text-center text-sm text-red-700">Не удалось загрузить организацию</p>
      </PageLayout>
    );
  }

  const title = company.name || 'Без названия';
  return (
    <PageLayout
      title={title}
      titleIcon={<Building2 className="h-6 w-6 shrink-0 text-gray-400" aria-hidden="true" />}
      subtitle={
        company.city ? <span data-testid="client-company-city">{company.city}</span> : undefined
      }
      breadcrumbs={[LIST_CRUMB, { label: title }]}
      backButton={{ href: '/companies' }}
      data-testid="client-company-page"
    >
      <div className="mb-4">
        <Link
          to={companyCrmHref(company.id)}
          className="text-sm font-medium text-blue-600 hover:underline"
        >
          Открыть в CRM
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <ClientCompanyRequisitesCard editor={editor} />
          <ClientCompanyContactsCard contacts={company.contacts ?? []} />
        </div>
        <div className="min-w-0 space-y-6">
          <ClientCompanyExtraCard extra={company.extra} />
        </div>
      </div>
    </PageLayout>
  );
}
